/**
 * Tool finite-state machine — TD-001 §4.7.3 / PRD-C TR3.
 *
 * Models the active drawing/selection tool. Every tool is reachable from every
 * state via a single `set/<tool>` event — no guards, no constraints. Production
 * wiring (ToolController translates `bus.uiActions$` → FSM event) lands in P3.
 */
export type ToolState =
  | 'select'
  | 'sticky'
  | 'textbox'
  | 'line'
  | 'rectangle'
  | 'ellipse'
  | 'checkbox'
  | 'person'
  | 'smiley'
  | 'image';

/** One FSM event per target tool. ToolController (P3) maps bus.uiActions$ → this event. */
export type ToolFsmEvent =
  | 'set/select'
  | 'set/sticky'
  | 'set/textbox'
  | 'set/line'
  | 'set/rectangle'
  | 'set/ellipse'
  | 'set/checkbox'
  | 'set/person'
  | 'set/smiley'
  | 'set/image';

/** Every tool is reachable from any state — direct switching without constraints. */
const ALL_TARGETS = {
  'set/select': 'select',
  'set/sticky': 'sticky',
  'set/textbox': 'textbox',
  'set/line': 'line',
  'set/rectangle': 'rectangle',
  'set/ellipse': 'ellipse',
  'set/checkbox': 'checkbox',
  'set/person': 'person',
  'set/smiley': 'smiley',
  'set/image': 'image',
} as const satisfies Readonly<Record<ToolFsmEvent, ToolState>>;

export const TOOL_TRANSITIONS = {
  select: ALL_TARGETS,
  sticky: ALL_TARGETS,
  textbox: ALL_TARGETS,
  line: ALL_TARGETS,
  rectangle: ALL_TARGETS,
  ellipse: ALL_TARGETS,
  checkbox: ALL_TARGETS,
  person: ALL_TARGETS,
  smiley: ALL_TARGETS,
  image: ALL_TARGETS,
} as const satisfies Readonly<Record<ToolState, Readonly<Record<ToolFsmEvent, ToolState>>>>;
